import { useLocalization } from "@fluent/react";
import { useState } from "react";
import { usePutApplicationApplicantDetailsQuery } from "@lending/hooks/queries/ApplicationApplicantDetails/usePutApplicationApplicantDetailsQuery";
import { useProductType } from "@lending/hooks/useProductType";
import { useCreateAutoLoanApplication } from "@lending/pages/Common/PersonalInfo/hooks/useCreateAutoLoanApplication";
import { useCreateCreditCardApplication } from "@lending/pages/Common/PersonalInfo/hooks/useCreateCreditCardApplication";
import { useCreatePersonalLoanApplication } from "@lending/pages/Common/PersonalInfo/hooks/useCreatePersonalLoanApplication";
import { useStore } from "../../../../../contexts/StoreContext";
import type { PersonalInfoReviewFormData } from "../components/PersonalInfoEditForm";

class ApplicationCreationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ApplicationCreationError";
  }
}

class AddressSaveError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AddressSaveError";
  }
}

interface ExistingApplicationInfo {
  applicationUuid: string;
  applicantDetailsId: string;
}

function useCreatePlaidApplication() {
  const { l10n } = useLocalization();
  const { data: storeData } = useStore();
  const productType = useProductType();

  const creditCardHandler = useCreateCreditCardApplication();
  const personalLoanHandler = useCreatePersonalLoanApplication();
  const autoLoanHandler = useCreateAutoLoanApplication();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const applicationCreateErrorMessage = l10n.getString(
    "error-application-create",
    null,
    "There was an error creating your application. Please try again."
  );
  const applicationUpdateErrorMessage = l10n.getString(
    "error-application-update",
    null,
    "There was an error updating your application. Please try again."
  );
  const addressSaveErrorMessage = l10n.getString(
    "error-address-save",
    null,
    "There was an error saving your address. Please try again."
  );

  const { mutateAsync: updateApplicantDetails } = usePutApplicationApplicantDetailsQuery();

  async function createNewApplication(data: PersonalInfoReviewFormData, captcha?: string | null) {
    const payload = {
      first_name: data.first_name,
      last_name: data.last_name,
      email: storeData?.email ?? "",
      phone: storeData?.phone ?? "",
      tax_id: data.tax_id,
      date_of_birth: data.date_of_birth,
    };

    if (!productType) {
      throw new ApplicationCreationError(applicationCreateErrorMessage);
    }

    let result;

    if (productType === "personal-loan") {
      result = await personalLoanHandler.createApplication(payload, captcha);
    } else if (productType === "auto-loan") {
      result = await autoLoanHandler.createApplication(payload, captcha);
    } else if (productType === "credit-card") {
      result = await creditCardHandler.createApplication(payload, captcha);
    } else {
      throw new ApplicationCreationError(applicationCreateErrorMessage);
    }
    if (!result) {
      throw new ApplicationCreationError(applicationCreateErrorMessage);
    }

    return result;
  }

  async function updateExistingApplication(
    data: PersonalInfoReviewFormData,
    existingInfo: ExistingApplicationInfo
  ) {
    await updateApplicantDetails({
      params: { path: { uuid: existingInfo.applicantDetailsId } },
      body: {
        application: existingInfo.applicationUuid,
        application_relationship: "primary",
        first_name: data.first_name,
        last_name: data.last_name,
        email: storeData?.email ?? "",
        phone: storeData?.phone ?? "",
        // A masked SSN (***-**-1234) from /sensitive means the saved one is unchanged, so leave it out.
        ...(data.tax_id.startsWith("*") ? {} : { tax_id: data.tax_id }),
        date_of_birth: data.date_of_birth,
        residential_address: {
          street_address: data.street,
          street_address_2: data.street2 ?? "",
          city: data.city,
          region_code: data.region,
          postal_code: data.postal_code,
          country_code: "US",
        },
      },
    });

    return {
      application: { id: existingInfo.applicationUuid },
      applicantDetails: { id: existingInfo.applicantDetailsId },
    };
  }

  async function submitApplication(
    data: PersonalInfoReviewFormData,
    existingInfo?: ExistingApplicationInfo,
    hasSensitiveFieldsChanged?: boolean,
    captcha?: string | null
  ) {
    setIsSubmitting(true);
    try {
      const needsNewApp = !existingInfo || hasSensitiveFieldsChanged;

      if (needsNewApp) {
        const { application, applicantDetails } = await createNewApplication(data, captcha);

        try {
          await updateApplicantDetails({
            params: { path: { uuid: applicantDetails.id } },
            body: {
              ...applicantDetails,
              residential_address: {
                street_address: data.street,
                street_address_2: data.street2 ?? "",
                city: data.city,
                region_code: data.region,
                postal_code: data.postal_code,
                country_code: "US",
              },
            },
          });
        } catch {
          throw new AddressSaveError(addressSaveErrorMessage);
        }

        return { application, applicantDetails };
      }

      try {
        return await updateExistingApplication(data, existingInfo);
      } catch {
        throw new ApplicationCreationError(applicationUpdateErrorMessage);
      }
    } catch (error) {
      if (error === "invalid recaptcha") {
        throw error;
      }
      if (error instanceof ApplicationCreationError || error instanceof AddressSaveError) {
        throw error;
      }
      throw new ApplicationCreationError(applicationCreateErrorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }

  return { submitApplication, isSubmitting };
}

export { useCreatePlaidApplication, ApplicationCreationError, AddressSaveError };
export type { ExistingApplicationInfo };
